import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	Divider,
	Tabs,
	Tab,
	IconButton,
	CircularProgress,
	DialogTitle,
} from '@mui/material';
import MyAccount from './MyAccount';
import DeviceWarranty from './DeviceWarranty';
import SitesAndGroups from './SitesAndGroups';
import PrimaryButton from '../button/PrimaryButton';
import Text from '../text';
import ApiService from 'utils/services/ApiService';
import { saveSwitchStateToDB } from 'utils/services/idbService';
import { useOfflineSwitch } from 'utils/helper/OfflineSwitchContext';
import { getSwitchStateFromDB } from '../../utils/services/idbService';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import { useSelector, useDispatch } from 'react-redux';
import { IReduxState } from 'utils/models/redux-states';
import { setUserInformation } from 'utils/redux/reducers/userReducer';
import { User } from 'utils/models/user';
import LabRequests from './LabRequests';
import { addEventLog } from 'utils/functions/eventLogFunctions';

const NAV_LABEL_MAP: [string[], string][] = [
	[["Bugcount® Analyzer", "Bugcount Analyzer Warranty Registration"], "Analyzer Warranty"],
	[["MANAGE SITES", "Sites & Groups"], "Site & Group Management"],
	[["LAB SERVICE REQUESTS", "COC"], "Lab Service Requests"],
	[["MY ACCOUNT", "My Account"], "My Account"],
];
function mapNavLabel(value: string): string | undefined {
	const match = NAV_LABEL_MAP.find(([aliases]) => aliases.includes(value));
	return match?.[1];
}


interface AdminSettingsDialogProps {
	isOpen: boolean;
	onClose: () => void;
	mode: 'online' | 'offline';
	user?: any;
	setActiveAdminTab: React.Dispatch<React.SetStateAction<number>>;
	activeAdminTab: number;
	isConnected: boolean;
	isAnalyzerConnected: boolean;
	handleConnectToPort: () => void;
}

const AdminSettingsDialog: React.FC<AdminSettingsDialogProps> = ({
	isOpen,
	onClose,
	mode,
	user,
	setActiveAdminTab,
	activeAdminTab,
	isConnected,
	isAnalyzerConnected,
	handleConnectToPort
}) => {
	const dispatch = useDispatch();
	const reduxState = useSelector((state: IReduxState) => state.user);
	const userData = reduxState?.userInformation;
	const reduxUpdateDataState = useSelector(
		(state: any) => state.updateReduxData
	);
	const isDataUpdated = reduxUpdateDataState.updateReduxData;
	const sendPopUpMessage = usePopupMessage();
	const [userDetails, setUserDetails] = useState<User>();
	const [isLoading, setIsLoading] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	const { offlineSwitchState, setOfflineSwitchState } = useOfflineSwitch();
	const settings = userData?.organizationVO?.id === 2 ? 
		['MANAGE SITES', 'Bugcount® Analyzer', 'MY ACCOUNT']
		:
		['MANAGE SITES', 'Bugcount® Analyzer', 'LAB SERVICE REQUESTS', 'MY ACCOUNT'];//, 'DATA CONTROL'];
	let localStorageFlag = localStorage.getItem('localStorage') ?? 'false';
	const [localSwitchState, setLocalSwitchState] =
		useState(localStorageFlag === 'false');

	useEffect(() => {
		// Initialize local switch state when the dialog opens
		if (isOpen) {
			setLocalSwitchState(localStorageFlag === 'false');
		}
	}, [isOpen, offlineSwitchState, user]);

	const handleSwitchChange = async (newState: boolean) => {
		setLocalSwitchState(newState);
		try {
			const result = await ApiService.syncServiceFlag(newState ? 0 : 1); //The backend field is named LOCAL_STORAGE, so we flip this
			localStorageFlag = JSON.stringify(!newState);
			localStorage.setItem(
				'localStorage',
				JSON.stringify(!newState)
			);
			if (result.status === 200 || result.status === 204) {
				sendPopUpMessage(
					'Success',
					`${
						newState
							? 'Sync with account enabled.'
							: 'Local storage only enabled.'
					}`
				);
			} else {
				throw Error('Error connecting with the server.');
			}
		} catch (error) {
			console.log(error);
			sendPopUpMessage(
				'Error',
				'Changes not saved. Please refresh your browser and try again.'
			);
		}
	};

	useEffect(() => {
		const updateOnlineStatus = () => {
			setIsOnline(navigator.onLine);
		};

		window.addEventListener('online', updateOnlineStatus);
		window.addEventListener('offline', updateOnlineStatus);

		return () => {
			window.removeEventListener('online', updateOnlineStatus);
			window.removeEventListener('offline', updateOnlineStatus);
		};
	}, [userDetails]);

	const saveMyAccount = async () => {
		try {
			const response = await ApiService.postUpdateUser(userDetails);
			if (response.status === 200) {
				localStorage.removeItem('currentUser');
				localStorage.removeItem('userName');
				localStorage.removeItem('userDetails');

				localStorage.setItem(
					'userDetails',
					JSON.stringify(userDetails)
				);
				localStorage.setItem(
					'userName',
					`${userDetails?.firstName} ${userDetails?.lastName}`
				);
			}
			dispatch(setUserInformation(userDetails));
			sendPopUpMessage('Success', 'Account updated successfully.');
		} catch (error) {
			console.error('Failed to update user:', error);
			sendPopUpMessage(
				'Error',
				'Changes not saved. Please refresh your browser and try again.'
			);
		} finally {
			setIsLoading(false);
		}
	};

	const saveDataControl = async () => {
		try {
			const username = localStorage.getItem('currentUser') ?? 'unknown';
			await saveSwitchStateToDB(
				'user_switch_state',
				localSwitchState,
				username
			);
			const newState = await getSwitchStateFromDB(
				'user_switch_state',
				username
			);
			setOfflineSwitchState(localSwitchState, username);
			if (newState === true && navigator.serviceWorker.controller) {
				navigator.serviceWorker.controller.postMessage({
					type: 'SYNC_FAILED_REQUESTS',
				});
			}
		} catch (error) {
			console.error('Failed to save switch state:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSave = async () => {
		setIsLoading(true);

		if (activeAdminTab === 3 || (activeAdminTab === 2 && userData?.organizationVO?.id === 2)) {
			await saveMyAccount();
		}
		// if (activeAdminTab === 2) {
			// await saveDataControl();
		// }
	};

	const handleClose = () => {
		addEventLog(`Administation Settings Closed`, "Administration", undefined, undefined);
		onClose();
		setLocalSwitchState(offlineSwitchState ?? localStorageFlag);
	};

	const handleUpdateUserDetails = (updatedDetails: any) => {
		setUserDetails(updatedDetails);
	};

	useEffect(() => {
		setUserDetails(userData);
	}, [isDataUpdated, activeAdminTab]);

	return (
		<Dialog
			open={isOpen}
			onClose={(event, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					onClose();
				}
			}}
			fullWidth
			maxWidth="md"
			id="main-menu"
			sx={{
				'& .MuiPaper-root': {
					overflow: 'hidden',
					maxHeight: '82vh',
					position: 'absolute',
					top: '50px',
					maxWidth: '1000px',
				},
			}}
		>
			<DialogTitle>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Text addStyle="!font-inter !font-bold !text-[#2C2C2C] !text-[24px] !mb-4">
						{activeAdminTab == 2 && userData?.organizationVO?.id !== 2 ? 'Lab Service Requests' : 'Administration'}
					</Text>
					<IconButton
						onClick={onClose}
						sx={{
							position: 'absolute',
							top: 24,
							right: 24,
							p: 0,
							zIndex: 1000,
						}}
						aria-label="close"
					>
						<img
							src="/assets/icons/Close.svg"
							alt="Close"
							style={{
								width: '20px',
								height: '20px',
							}}
						/>
					</IconButton>
				</div>

				<Tabs
				
					value={activeAdminTab}
					onChange={(event, newValue) => {
						const newVal = mapNavLabel(settings[newValue]);
						addEventLog(`${newVal} Opened`, "Administration", undefined, undefined);
						setActiveAdminTab(newValue);
					}}
					sx={{
						'& .MuiTabs-indicator': {
							backgroundColor: '#0092C8',
						},
					}}
				>
					{settings.map((setting) => (
						((setting === "Bugcount® Analyzer" || setting === "LAB SERVICE REQUESTS") && !navigator.onLine) ? (
							<Tab key={setting} label={setting} disabled={!navigator.onLine} />
						) : (
							<Tab key={setting} label={setting} />
						)
					))}
				</Tabs>
				<Divider />
			</DialogTitle>
			<DialogContent
				sx={{
					paddingBottom: activeAdminTab === 0 ? '0px' : '',
				}}
			>
				{activeAdminTab === 0 && <SitesAndGroups />}
				{activeAdminTab === 3 && (
					<MyAccount
						mode={isOnline ? 'online' : 'offline'}
						userDetails={userData}
						onUpdateUserDetails={handleUpdateUserDetails}
					/>
				)}
				{activeAdminTab === 1 && (
					<DeviceWarranty
						mode={isOnline ? 'online' : 'offline'}
						userDetails={userData}
						handleConnectToPort={handleConnectToPort}
						isConnected={isConnected}
						isAnalyzerConnected={isAnalyzerConnected}
					/>
				)}
				{activeAdminTab === 2 && (
					userData?.organizationVO?.id === 2 ? (
						<MyAccount
							mode={isOnline ? 'online' : 'offline'}
							userDetails={userData}
							onUpdateUserDetails={handleUpdateUserDetails}
						/>
					) : (
						<LabRequests
							isChampionX={userData?.organizationVO?.id === 2}
						/>
					)
				)}
				{/* {activeAdminTab === 5 && (
					<DataControlTab
						switchState={localSwitchState}
						onSwitchChange={handleSwitchChange}
					/>
				)} */}
			</DialogContent>
			<DialogActions
				sx={{
					paddingTop: activeAdminTab === 0 ? '0px' : '',
				}}
			>
				{activeAdminTab !== 0 
				&& activeAdminTab !== 1 
				&& (activeAdminTab !== 2 || userData?.organizationVO?.id === 2)
				&& activeAdminTab !== 4 && (
					<>
						<Button
							onClick={handleClose}
							color="primary"
							className="!text-[#9EACB4]"
						>
							CANCEL
						</Button>
						{isLoading ? (
							<CircularProgress
								sx={{ color: '#0092C8' }}
								size={22}
								className="!mr-5"
							/>
						) : (
							<PrimaryButton
								addStyle="!h-[36px] !w-[68px]"
								onClick={handleSave}
								disabled={activeAdminTab === 0 && !isOnline}
							>
								SAVE
							</PrimaryButton>
						)}
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default AdminSettingsDialog;
